(() => {
  if (window.__eventsRalliesEvergreenBound) return;
  window.__eventsRalliesEvergreenBound = true;
  const allowedAnalytics = new Set([
    'event_view',
    'official_registration_click',
    'related_product_click',
    'add_to_cart',
    'purchase',
  ]);
  const subscriberTokenKey = 'events_rallies_subscriber_token';
  const subscriberSessionUrl = '/apps/events-interactions/subscriber-session';
  const subscriberRenewalWindowSeconds = 7 * 24 * 60 * 60;
  const roots = document.querySelectorAll('[data-events-directory]');
  if (roots.length) document.body.classList.add('events-rallies-page');

  function finiteCount(value) {
    const count = Number(value);
    return Number.isFinite(count) ? Math.max(0, count) : 0;
  }

  function canonicalEventUrl(input) {
    const url = new URL(input, window.location.origin);
    if (!['http:', 'https:'].includes(url.protocol)) throw new Error('invalid_share_url');
    url.search = '';
    url.hash = '';
    return url.toString();
  }

  function shareTargets(input, title = '') {
    const url = canonicalEventUrl(input);
    const encodedUrl = encodeURIComponent(url);
    const encodedTitle = encodeURIComponent(title);
    const encodedMessage = encodeURIComponent(`${title ? `${title} — ` : ''}${url}`);
    return {
      copy: url,
      facebook: `https://www.facebook.com/sharer/sharer.php?u=${encodedUrl}`,
      x: `https://x.com/intent/post?url=${encodedUrl}&text=${encodedTitle}`,
      linkedin: `https://www.linkedin.com/sharing/share-offsite/?url=${encodedUrl}`,
      pinterest: `https://www.pinterest.com/pin/create/button/?url=${encodedUrl}&description=${encodedTitle}`,
      whatsapp: `https://api.whatsapp.com/send?text=${encodedMessage}`,
      telegram: `https://t.me/share/url?url=${encodedUrl}&text=${encodedTitle}`,
      line: `https://social-plugins.line.me/lineit/share?url=${encodedUrl}`,
      email: `mailto:?subject=${encodedTitle}&body=${encodedMessage}`,
    };
  }

  async function copyShareLink(value) {
    if (navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(value);
      return;
    }
    const field = document.createElement('textarea');
    field.value = value;
    field.setAttribute('readonly', '');
    field.style.position = 'fixed';
    field.style.opacity = '0';
    document.body.append(field);
    field.select();
    document.execCommand('copy');
    field.remove();
  }

  function closeShareDialog(root) {
    const overlay = root.querySelector('[data-events-share-overlay]');
    if (!overlay || overlay.hidden) return;
    overlay.hidden = true;
    document.body.classList.remove('events-share-open');
    root._eventsShareTrigger?.focus();
  }

  function closeCommentsDialog(root) {
    const overlay = root.querySelector('[data-events-comments-overlay]');
    if (!overlay || overlay.hidden) return;
    overlay.hidden = true;
    document.body.classList.remove('events-comments-open');
    root._eventsCommentsTrigger?.focus();
  }

  function createCommentElement(comment) {
    const article = document.createElement('article');
    article.className = 'events-comments-item';
    const avatar = document.createElement('span');
    avatar.className = 'events-comments-avatar';
    avatar.textContent = String(comment.displayName || 'M').slice(0, 1).toUpperCase();
    const content = document.createElement('div');
    const header = document.createElement('header');
    const name = document.createElement('strong');
    name.textContent = comment.displayName || 'Member';
    const time = document.createElement('time');
    time.dateTime = comment.publishedAt || '';
    time.textContent = comment.publishedAt ? new Intl.DateTimeFormat(document.documentElement.lang || 'en').format(new Date(comment.publishedAt)) : '';
    const bodyElement = document.createElement('p');
    bodyElement.textContent = comment.body || '';
    header.append(name, time);
    content.append(header, bodyElement);
    article.append(avatar, content);
    return article;
  }

  function renderComments(root, comments) {
    const list = root.querySelector('[data-events-comments-list]');
    if (!list || !Array.isArray(comments) || comments.length === 0) return;
    list.replaceChildren();
    comments.forEach((comment) => list.append(createCommentElement(comment)));
  }

  async function hydrateReactionSummaries(root) {
    const cardsByHandle = new Map();
    root.querySelectorAll('[data-event-card]').forEach((card) => {
      const handle = card.dataset.eventId;
      if (!handle) return;
      const cards = cardsByHandle.get(handle) || [];
      cards.push(card);
      cardsByHandle.set(handle, cards);
    });
    const api = root.dataset.commentsApiUrl || '/apps/events-interactions';
    const subscriberToken = getSubscriberToken(root);
    await Promise.all([...cardsByHandle].map(async ([handle, cards]) => {
      try {
        const separator = api.includes('?') ? '&' : '?';
        const headers = { accept: 'application/json' };
        if (subscriberToken) headers['x-events-subscriber-token'] = subscriberToken;
        const response = await fetch(`${api}${separator}eventHandle=${encodeURIComponent(handle)}&limit=1`, {
          credentials: 'same-origin', headers,
        });
        if (!response.ok) return;
        const summary = await response.json();
        const liked = summary.liked === true;
        const count = finiteCount(summary.likeCount);
        cards.forEach((card) => {
          card.querySelector('[data-event-like]')?.setAttribute('aria-pressed', String(liked));
          card.querySelector('[data-event-like-count]')?.replaceChildren(String(count));
        });
      } catch {
        /* Initial reaction hydration is optional and never blocks editorial content. */
      }
    }));
  }

  async function loadComments(root, card) {
    const handle = card?.dataset.eventId;
    if (!handle) return;
    try {
      const api = root.dataset.commentsApiUrl || '/apps/events-interactions';
      const separator = api.includes('?') ? '&' : '?';
      const sortQuery = root.querySelector('[data-events-comments-sort]')?.value === 'newest' ? '&sort=newest' : '';
      const url = new URL(`${api}${separator}eventHandle=${encodeURIComponent(handle)}&limit=20${sortQuery}`, window.location.origin);
      const subscriberToken = getSubscriberToken(root);
      const headers = { accept: 'application/json' };
      if (subscriberToken) headers['x-events-subscriber-token'] = subscriberToken;
      const response = await fetch(url, { credentials: 'same-origin', headers });
      if (!response.ok) return;
      const summary = await response.json();
      const commentCount = finiteCount(summary.commentCount);
      const likeCount = finiteCount(summary.likeCount);
      const count = root.querySelector('[data-events-comments-count]');
      if (count) count.textContent = `${commentCount} comments`;
      card.querySelector('[data-event-comment-count]')?.replaceChildren(String(commentCount));
      card.querySelector('[data-event-like-count]')?.replaceChildren(String(likeCount));
      card.querySelector('[data-event-like]')?.setAttribute('aria-pressed', String(summary.liked === true));
      renderComments(root, summary.comments);
    } catch {
      /* The editorial page remains usable if the optional interaction service is offline. */
    }
  }

  function openCommentsDialog(root, trigger) {
    const card = trigger.closest('[data-event-card]');
    const overlay = root.querySelector('[data-events-comments-overlay]');
    if (!overlay || !card) return;
    root._eventsCommentsTrigger = trigger;
    root._eventsCommentsCard = card;
    const title = card.querySelector('.event-card__title')?.textContent?.trim() || document.title;
    const displayedCount = trigger.querySelector('span')?.textContent?.trim() || '0';
    overlay.querySelector('[data-events-comments-title]').textContent = title;
    overlay.querySelector('[data-events-comments-count]').textContent = `${displayedCount} comments`;
    overlay.hidden = false;
    document.body.classList.add('events-comments-open');
    overlay.querySelector('[data-events-comments-close]')?.focus();
    loadComments(root, card);
  }

  function commentMessages(root) {
    return {
      subscribing: root.dataset.commentSubscribingMessage || 'Subscribing…',
      subscribed: root.dataset.commentSubscribeSuccessMessage || 'Subscription successful. You can now write a comment.',
      publishing: root.dataset.commentPublishingMessage || 'Publishing…',
      published: root.dataset.commentPublishedMessage || 'Your comment is published.',
      rejected: root.dataset.commentRejectedMessage || 'Your comment could not be published. Check it and try again.',
      rateLimited: root.dataset.commentRateLimitedMessage || 'Please wait before trying again.',
      expired: root.dataset.commentExpiredMessage || 'Your subscriber session expired. Subscribe again to continue.',
      unavailable: root.dataset.commentUnavailableMessage || 'Comments are temporarily unavailable. Please try again.',
    };
  }

  async function persistLike(like) {
    if (like.disabled) return;
    const root = like.closest('[data-events-directory]');
    const card = like.closest('[data-event-card]');
    const eventHandle = card?.dataset.eventId;
    const subscriberToken = root ? getSubscriberToken(root) : null;
    if (!root || !card || !eventHandle || !subscriberToken) {
      if (root && card) {
        showSubscriptionForm(root, commentMessages(root).expired);
        openCommentsDialog(root, like);
      }
      return;
    }

    const count = like.querySelector('[data-event-like-count]');
    const previousPressed = like.getAttribute('aria-pressed') === 'true';
    const previousCount = finiteCount(count?.textContent);
    const optimisticPressed = !previousPressed;
    like.disabled = true;
    like.setAttribute('aria-busy', 'true');
    like.setAttribute('aria-pressed', String(optimisticPressed));
    if (count) count.textContent = String(Math.max(0, previousCount + (optimisticPressed ? 1 : -1)));

    try {
      const response = await fetch(root.dataset.commentsApiUrl || '/apps/events-interactions', {
        method: 'POST',
        credentials: 'same-origin',
        headers: {
          accept: 'application/json',
          'content-type': 'application/json',
          'idempotency-key': crypto.randomUUID(),
        },
        body: JSON.stringify({
          action: optimisticPressed ? 'like' : 'unlike',
          eventHandle,
          subscriberToken,
        }),
      });
      if (!response.ok) throw requestFailure(response.status);
      const summary = await response.json();
      if (typeof summary.liked !== 'boolean' || !Number.isFinite(Number(summary.count))) throw requestFailure(503);
      like.setAttribute('aria-pressed', String(summary.liked));
      if (count) count.textContent = String(finiteCount(summary.count));
    } catch (error) {
      like.setAttribute('aria-pressed', String(previousPressed));
      if (count) count.textContent = String(previousCount);
      if (error.status === 401) removeSubscriberToken(root);
    } finally {
      like.disabled = false;
      like.removeAttribute('aria-busy');
    }
  }

  function safeCommentMessage(root, status) {
    const messages = commentMessages(root);
    const byStatus = {
      400: messages.rejected,
      401: messages.expired,
      403: messages.rejected,
      409: messages.rejected,
      429: messages.rateLimited,
      503: messages.unavailable,
    };
    return byStatus[status] || messages.unavailable;
  }

  function setCommentStatus(element, message, state = '') {
    if (!element) return;
    element.textContent = message;
    element.dataset.state = state;
  }

  function getSubscriberToken(root) {
    if (root._eventsSubscriberToken) return root._eventsSubscriberToken;
    try {
      return localStorage.getItem(subscriberTokenKey);
    } catch {
      return null;
    }
  }

  function storeSubscriberToken(root, token) {
    root._eventsSubscriberToken = token;
    try {
      localStorage.setItem(subscriberTokenKey, token);
    } catch {
      /* The new session remains usable in this open page when storage is unavailable. */
    }
  }

  function removeSubscriberToken(root) {
    root._eventsSubscriberToken = null;
    try {
      localStorage.removeItem(subscriberTokenKey);
    } catch {
      /* There is no additional credential state to clear. */
    }
  }

  function decodeJwtExpiry(token) {
    try {
      const encoded = token.split('.')[1];
      if (!encoded) return 0;
      const normalized = encoded.replace(/-/g, '+').replace(/_/g, '/');
      const padded = normalized.padEnd(Math.ceil(normalized.length / 4) * 4, '=');
      const expiry = Number(JSON.parse(atob(padded)).exp);
      return Number.isFinite(expiry) ? expiry : 0;
    } catch {
      return 0;
    }
  }

  function showSubscriptionForm(root, message = '') {
    const subscribeForm = root.querySelector('[data-events-comment-subscribe-form]');
    const commentForm = root.querySelector('[data-events-subscriber-comment-form]');
    if (!subscribeForm || !commentForm) return;
    commentForm.hidden = true;
    subscribeForm.hidden = false;
    setCommentStatus(subscribeForm.querySelector('[data-events-comment-subscribe-status]'), message, message ? 'error' : '');
  }

  function showCommentComposer(root, focus = false) {
    const subscribeForm = root.querySelector('[data-events-comment-subscribe-form]');
    const commentForm = root.querySelector('[data-events-subscriber-comment-form]');
    if (!subscribeForm || !commentForm) return;
    subscribeForm.hidden = true;
    commentForm.hidden = false;
    setCommentStatus(subscribeForm.querySelector('[data-events-comment-subscribe-status]'), '');
    if (focus) commentForm.querySelector('textarea')?.focus({ preventScroll: true });
  }

  async function renewSubscriberSession(root, subscriberToken) {
    try {
      const response = await fetch(subscriberSessionUrl, {
        method: 'POST',
        credentials: 'same-origin',
        headers: {
          accept: 'application/json',
          'content-type': 'application/json',
          'idempotency-key': crypto.randomUUID(),
        },
        body: JSON.stringify({ action: 'renew', subscriberToken }),
      });
      if (!response.ok) {
        if (response.status === 401) {
          removeSubscriberToken(root);
          showSubscriptionForm(root, commentMessages(root).expired);
        }
        return;
      }
      const session = await response.json();
      if (typeof session.token !== 'string' || !session.token) return;
      storeSubscriberToken(root, session.token);
    } catch {
      /* Renewal stays silent while the current signed session is still valid. */
    }
  }

  function initializeSubscriberSession(root) {
    const subscriberToken = getSubscriberToken(root);
    if (!subscriberToken) {
      showSubscriptionForm(root);
      return;
    }
    const expiresAt = decodeJwtExpiry(subscriberToken);
    const now = Math.floor(Date.now() / 1000);
    showCommentComposer(root);
    if (!expiresAt || expiresAt - now <= subscriberRenewalWindowSeconds) {
      renewSubscriberSession(root, subscriberToken);
    }
  }

  function requestFailure(status) {
    const error = new Error('request_failed');
    error.status = status;
    return error;
  }

  async function submitSubscriberSession(root, subscribeForm) {
    const emailInput = subscribeForm.querySelector('input[type="email"]');
    const submitButton = subscribeForm.querySelector('button[type="submit"]');
    const status = subscribeForm.querySelector('[data-events-comment-subscribe-status]');
    if (!emailInput || !submitButton || submitButton.disabled) return;
    const email = emailInput.value.trim();
    emailInput.value = '';
    if (!email) {
      showSubscriptionForm(root, commentMessages(root).rejected);
      return;
    }
    submitButton.disabled = true;
    subscribeForm.setAttribute('aria-busy', 'true');
    setCommentStatus(status, commentMessages(root).subscribing, 'loading');
    try {
      const response = await fetch(subscriberSessionUrl, {
        method: 'POST',
        credentials: 'same-origin',
        headers: {
          accept: 'application/json',
          'content-type': 'application/json',
          'idempotency-key': crypto.randomUUID(),
        },
        body: JSON.stringify({ action: 'subscribe', email }),
      });
      if (response.status !== 201) throw requestFailure(response.status);
      const session = await response.json();
      if (typeof session.token !== 'string' || !session.token) throw requestFailure(503);
      storeSubscriberToken(root, session.token);
      showCommentComposer(root, true);
      setCommentStatus(
        root.querySelector('[data-events-subscriber-comment-form] [data-events-comment-status]'),
        commentMessages(root).subscribed,
        'success',
      );
    } catch (error) {
      showSubscriptionForm(root, safeCommentMessage(root, error.status));
    } finally {
      submitButton.disabled = false;
      subscribeForm.removeAttribute('aria-busy');
    }
  }

  function incrementVisibleCommentCount(root) {
    const dialogCount = root.querySelector('[data-events-comments-count]');
    const triggerCount = root._eventsCommentsTrigger?.querySelector('span');
    const currentCount = Number.parseInt(dialogCount?.textContent || triggerCount?.textContent || '0', 10);
    const nextCount = Number.isFinite(currentCount) ? currentCount + 1 : 1;
    if (dialogCount) dialogCount.textContent = `${nextCount} comments`;
    if (triggerCount) triggerCount.textContent = String(nextCount);
  }

  async function submitSubscriberComment(root, commentForm) {
    const textarea = commentForm.querySelector('textarea');
    const submitButton = commentForm.querySelector('button[type="submit"]');
    const status = commentForm.querySelector('[data-events-comment-status]');
    const subscriberToken = getSubscriberToken(root);
    const eventHandle = root._eventsCommentsCard?.dataset.eventId || '';
    const languageCode = root._eventsCommentsCard?.dataset.eventLanguage || document.documentElement.lang || 'en';
    if (!textarea || !submitButton || submitButton.disabled) return;
    const body = textarea.value.trim();
    if (!body || !eventHandle) {
      setCommentStatus(status, commentMessages(root).rejected, 'error');
      return;
    }
    if (!subscriberToken) {
      showSubscriptionForm(root, commentMessages(root).expired);
      return;
    }
    submitButton.disabled = true;
    commentForm.setAttribute('aria-busy', 'true');
    setCommentStatus(status, commentMessages(root).publishing, 'loading');
    try {
      const commentsApiUrl = root.dataset.commentsApiUrl || '/apps/events-interactions';
      const response = await fetch(commentsApiUrl, {
        method: 'POST',
        credentials: 'same-origin',
        headers: {
          accept: 'application/json',
          'content-type': 'application/json',
          'idempotency-key': crypto.randomUUID(),
        },
        body: JSON.stringify({
          action: 'comment',
          eventHandle,
          languageCode,
          body,
          subscriberToken: subscriberToken,
        }),
      });
      if (response.status !== 201) throw requestFailure(response.status);
      const comment = await response.json();
      if (comment.status !== 'published' || typeof comment.body !== 'string') throw requestFailure(503);
      const list = root.querySelector('[data-events-comments-list]');
      if (!list) throw requestFailure(503);
      const article = createCommentElement(comment);
      list.querySelector(':scope > p')?.remove();
      list.prepend(article);
      incrementVisibleCommentCount(root);
      textarea.value = '';
      setCommentStatus(status, commentMessages(root).published, 'success');
    } catch (error) {
      if (error.status === 401) {
        removeSubscriberToken(root);
        showSubscriptionForm(root, commentMessages(root).expired);
      } else {
        setCommentStatus(status, safeCommentMessage(root, error.status), 'error');
      }
    } finally {
      submitButton.disabled = false;
      commentForm.removeAttribute('aria-busy');
    }
  }

  function openShareDialog(root, trigger) {
    const card = trigger.closest('[data-event-card]');
    const rawUrl = card?.dataset.eventCanonicalUrl || card?.querySelector('.event-card__title a')?.href || window.location.href;
    const title = card?.querySelector('.event-card__title')?.textContent?.trim() || document.title;
    const targets = shareTargets(rawUrl, title);
    const overlay = root.querySelector('[data-events-share-overlay]');
    if (!overlay) return;
    root._eventsShareTrigger = trigger;
    root._eventsShareTargets = targets;
    root._eventsShareTitle = title;
    overlay.querySelector('[data-events-share-title]').textContent = title;
    overlay.querySelector('[data-events-share-status]').textContent = '';
    overlay.querySelectorAll('a[data-event-share-channel]').forEach((link) => {
      const target = targets[link.dataset.eventShareChannel];
      if (target) link.href = target;
    });
    overlay.hidden = false;
    document.body.classList.add('events-share-open');
    overlay.querySelector('[data-events-share-close]')?.focus();
  }

  function analyticsPayload(element) {
    const context = element?.closest('[data-event-id]') || document.querySelector('[data-event-detail]');
    const payload = {
      event_id: context?.dataset.eventId || '',
      event_country: context?.dataset.eventCountry || '',
      language: context?.dataset.eventLanguage || '',
      event_type: context?.dataset.eventType || '',
      page_path: window.location.pathname,
    };
    if (element?.dataset.productId) payload.product_id = element.dataset.productId;
    return payload;
  }

  function emitAnalytics(name, element) {
    if (!allowedAnalytics.has(name)) return;
    const payload = analyticsPayload(element);
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({ event: name, ...payload });
    if (window.Shopify?.analytics?.publish) window.Shopify.analytics.publish(name, payload);
  }

  function valuesFromHash(root) {
    const filters = new URLSearchParams(window.location.hash.replace(/^#/, ''));
    if (root.dataset.lockedCountry) filters.set('country', root.dataset.lockedCountry);
    if (root.dataset.lockedType) filters.set('type', root.dataset.lockedType);
    return filters;
  }

  function matches(card, filters) {
    for (const key of ['country', 'type', 'cost']) {
      const expected = (filters.get(key) || '').trim().toLowerCase();
      if (expected && card.dataset[key] !== expected) return false;
    }
    const start = filters.get('start') || '';
    const end = filters.get('end') || '';
    const query = (filters.get('q') || '').trim().toLowerCase();
    if (start && card.dataset.end < start) return false;
    if (end && card.dataset.start > end) return false;
    if (query && !`${card.dataset.search || ''} ${card.dataset.title || ''} ${card.dataset.city || ''}`.includes(query)) return false;
    return true;
  }

  function apply(root) {
    const filters = valuesFromHash(root);
    let visible = 0;
    root.querySelectorAll('[data-event-card]').forEach((card) => {
      const show = matches(card, filters);
      card.hidden = !show;
      if (show) visible += 1;
    });
    const empty = root.querySelector('[data-events-empty]');
    if (empty) empty.hidden = visible !== 0;
    root.querySelectorAll('[data-event-filter]').forEach((input) => {
      if (input.type === 'checkbox') input.checked = filters.get(input.name) === input.value;
      else input.value = filters.get(input.name) || '';
    });
  }

  function initializeRoot(root) {
    if (root._eventsRalliesInitialized) return;
    root._eventsRalliesInitialized = true;
    const subscribeToggle = root.querySelector('[data-events-subscribe-toggle]');
    const subscribePanel = root.querySelector('[data-events-subscribe-panel]');
    subscribeToggle?.addEventListener('click', () => {
      if (!subscribePanel) return;
      const willOpen = subscribePanel.hidden;
      subscribePanel.hidden = !willOpen;
      subscribeToggle.setAttribute('aria-expanded', String(willOpen));
      if (willOpen) subscribePanel.querySelector('input')?.focus();
    });
    if (subscribePanel && !subscribePanel.hidden) {
      subscribeToggle?.setAttribute('aria-expanded', 'true');
      subscribePanel.querySelector('[data-events-subscribe-status], [role="alert"]')?.focus();
    }
    const filterDrawer = root.querySelector('[data-events-filter-drawer]');
    const moreToggle = root.querySelector('[data-events-more-toggle]');
    moreToggle?.addEventListener('click', () => {
      if (!filterDrawer) return;
      filterDrawer.open = !filterDrawer.open;
      moreToggle.setAttribute('aria-expanded', String(filterDrawer.open));
      if (filterDrawer.open) filterDrawer.querySelector('input, select')?.focus();
    });
    const results = root.querySelector('[data-events-results]');
    const originalCards = results ? Array.from(results.querySelectorAll('[data-event-card]')) : [];
    root.querySelector('[data-events-sort]')?.addEventListener('change', (event) => {
      if (!results) return;
      const cards = [...originalCards];
      if (event.currentTarget.value === 'newest') cards.reverse();
      else cards.sort((a, b) => (a.dataset.start || '').localeCompare(b.dataset.start || ''));
      cards.forEach((card) => results.append(card));
    });
    root.querySelectorAll('[data-events-window], [data-events-type-shortcut]').forEach((button) => {
      button.addEventListener('click', () => {
        const filters = new URLSearchParams(window.location.hash.replace(/^#/, ''));
        filters.delete('start');
        filters.delete('end');
        filters.delete('type');
        const today = new Date();
        const iso = (date) => date.toISOString().slice(0, 10);
        if (button.dataset.eventsWindow === 'weekend') {
          const saturday = new Date(today);
          saturday.setDate(today.getDate() + ((6 - today.getDay() + 7) % 7));
          const sunday = new Date(saturday);
          sunday.setDate(saturday.getDate() + 1);
          filters.set('start', iso(saturday));
          filters.set('end', iso(sunday));
        } else if (button.dataset.eventsWindow === '30') {
          const ending = new Date(today);
          ending.setDate(today.getDate() + 30);
          filters.set('start', iso(today));
          filters.set('end', iso(ending));
        } else if (button.dataset.eventsWindow === 'year') {
          filters.set('start', iso(today));
          filters.set('end', `${today.getFullYear()}-12-31`);
        } else if (button.dataset.eventsTypeShortcut) {
          filters.set('type', button.dataset.eventsTypeShortcut);
        }
        const hash = filters.toString();
        history.replaceState(null, '', `${window.location.pathname}${hash ? `#${hash}` : ''}`);
        root.querySelectorAll('[data-events-window], [data-events-type-shortcut]').forEach((item) => item.classList.toggle('is-active', item === button));
        apply(root);
      });
    });
    root.querySelector('[data-events-quick-search]')?.addEventListener('input', (event) => {
      const filters = new URLSearchParams(window.location.hash.replace(/^#/, ''));
      const query = event.currentTarget.value.trim();
      if (query) filters.set('q', query);
      else filters.delete('q');
      const hash = filters.toString();
      history.replaceState(null, '', `${window.location.pathname}${hash ? `#${hash}` : ''}`);
      apply(root);
    });
    root.querySelector('[data-events-filter-form]')?.addEventListener('submit', (event) => {
      event.preventDefault();
      const filters = new URLSearchParams();
      new FormData(event.currentTarget).forEach((value, key) => {
        const normalized = String(value).trim();
        if (normalized) filters.set(key, normalized);
      });
      const hash = filters.toString();
      history.replaceState(null, '', `${window.location.pathname}${hash ? `#${hash}` : ''}`);
      apply(root);
    });
    apply(root);

    const shareOverlay = root.querySelector('[data-events-share-overlay]');
    shareOverlay?.addEventListener('click', async (event) => {
      if (event.target === shareOverlay || event.target.closest('[data-events-share-close]')) {
        closeShareDialog(root);
        return;
      }
      const control = event.target.closest('[data-event-share-channel]');
      if (!control) return;
      const channel = control.dataset.eventShareChannel;
      const targets = root._eventsShareTargets;
      if (!targets) return;
      if (channel === 'copy') {
        await copyShareLink(targets.copy);
        shareOverlay.querySelector('[data-events-share-status]').textContent = control.dataset.copiedLabel || 'Link copied.';
      } else if (channel === 'native') {
        if (navigator.share) {
          await navigator.share({ title: root._eventsShareTitle, url: targets.copy }).catch(() => {});
        } else {
          await copyShareLink(targets.copy);
          shareOverlay.querySelector('[data-events-share-status]').textContent = control.dataset.copiedLabel || 'Link copied.';
        }
      } else if (channel === 'messenger') {
        if (navigator.share) {
          await navigator.share({ title: root._eventsShareTitle, url: targets.copy }).catch(() => {});
        } else {
          await copyShareLink(targets.copy);
          shareOverlay.querySelector('[data-events-share-status]').textContent = control.dataset.messengerFallback || 'Link copied. Paste it into Messenger.';
        }
      }
    });
    const commentsOverlay = root.querySelector('[data-events-comments-overlay]');
    commentsOverlay?.querySelector('[data-events-comment-subscribe-form]')?.addEventListener('submit', (event) => {
      event.preventDefault();
      submitSubscriberSession(root, event.currentTarget);
    });
    commentsOverlay?.addEventListener('click', (event) => {
      if (event.target === commentsOverlay || event.target.closest('[data-events-comments-close]')) closeCommentsDialog(root);
    });
    commentsOverlay?.querySelector('[data-events-comments-sort]')?.addEventListener('change', () => loadComments(root, root._eventsCommentsCard));
    commentsOverlay?.querySelector('[data-events-comment-form]')?.addEventListener('submit', async (event) => {
      event.preventDefault();
      await submitSubscriberComment(root, event.currentTarget);
    });
    initializeSubscriberSession(root);
    hydrateReactionSummaries(root);
  }

  roots.forEach(initializeRoot);

  document.addEventListener('click', async (event) => {
    const like = event.target.closest('[data-event-like]');
    if (like) await persistLike(like);
    const share = event.target.closest('[data-event-share]');
    if (share) {
      const root = share.closest('[data-events-directory]');
      if (root) openShareDialog(root, share);
    }
    const comments = event.target.closest('[data-event-comments]');
    if (comments) {
      const root = comments.closest('[data-events-directory]');
      if (root) openCommentsDialog(root, comments);
      document.dispatchEvent(new CustomEvent('events:open-comments', { detail: { card: comments.closest('[data-event-card]') } }));
    }
  });

  document.addEventListener('keydown', (event) => {
    if (event.key !== 'Escape') return;
    roots.forEach((root) => { closeShareDialog(root); closeCommentsDialog(root); });
  });

  window.addEventListener('hashchange', () => roots.forEach(apply));
  document.addEventListener('shopify:section:load', (event) => {
    if (event.target?.matches?.('[data-events-directory]')) initializeRoot(event.target);
    event.target?.querySelectorAll?.('[data-events-directory]').forEach(initializeRoot);
  });

  document.addEventListener('click', (event) => {
    const target = event.target.closest('[data-event-analytics]');
    if (target) {
      emitAnalytics(target.dataset.eventAnalytics, target);
      if (target.dataset.eventAnalytics === 'related_product_click') {
        sessionStorage.setItem('events_rallies_context', JSON.stringify(analyticsPayload(target)));
      }
    }
  });
  const detail = document.querySelector('[data-event-detail]');
  if (detail) emitAnalytics('event_view', detail);
})();
